import type {
  LiveList,
  LiveMap,
  LiveObject,
  LsonObject,
} from '@liveblocks/client'
import { LiveblocksProvider as LiveblocksProviderBase } from '@liveblocks/react/suspense'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { useQueryClient } from '@tanstack/react-query'
import type { JSX, ReactNode } from 'react'
import { authorizeLiveblocksSession } from '#src/components/authorizeLiveblocksSession.ts'
import { rememberLiveblocksRoomId } from '#src/components/liveblocksMedplumRefresh.ts'
import { ReconnectLiveblocksOnMedplumRefresh } from '#src/components/ReconnectLiveblocksOnMedplumRefresh.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { searchOptions } from '#src/query-options/searchOptions.ts'

/* TODO: split into per-room-type contexts via `createRoomContext` when a
 * second room type needs a non-trivial Storage shape. Liveblocks' own
 * recommendation for "different rooms with different storage" is one
 * `createRoomContext` per room type — the global `interface Liveblocks`
 * pattern treats every room identically. For now we mark each field optional and
 * let rooms that don't pass `initialStorage` (care plan, desks) fall through to
 * the runtime check inside `CollaborativeEditor`. */
declare global {
  interface Liveblocks {
    Storage: {
      /* Tracks which `field` names in this room's Y.Doc have already had
       * `CollaborativeEditor`'s initial content seeded into them. Liveblocks'
       * own `useLiveblocksExtension({ initialContent })` only seeds when the
       * whole Y.Doc is empty, which breaks per-field seeding in rooms shared
       * across multiple editors (e.g., participant rooms hosting memo +
       * visit notes). Rooms that opt in by passing `initialStorage` get
       * per-field seeding; rooms that don't fall back to Liveblocks' default
       * behavior. */
      seededFields?: LiveMap<string, boolean>
      /* Desk rooms (`DocumentReference/<deskId>`) hold the canvas here: the
       * `@liveblocks/zustand` middleware's `storageMapping` writes the React
       * Flow `nodes`/`edges` arrays straight onto the root. Typed as opaque
       * LSON rather than mirroring `@xyflow/react`'s `Node`, because the
       * middleware persists whatever shape the canvas happens to hold —
       * readers narrow at the boundary (see `toDeskStickyNoteNode`). */
      nodes?: LiveList<LiveObject<LsonObject>>
      edges?: LiveList<LiveObject<LsonObject>>
    }
  }
}

export function LiveblocksProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const { seenApi } = useSeenApi()
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  return (
    <LiveblocksProviderBase
      authEndpoint={async (room) => {
        if (room) {
          rememberLiveblocksRoomId(room)
        }
        return authorizeLiveblocksSession(seenApi, {
          roomId: room,
        })
      }}
      resolveUsers={async ({ userIds }) => {
        const users = await queryClient.fetchQuery(
          searchOptions(medplum, ExtendedPractitioner, { _id: userIds })
        )
        return users.map((user) => ({
          id: user.id,
          name: user.name,
          avatar: user.photoUrl,
        }))
      }}
      preventUnsavedChanges
      throttle={16} // Updates every 16ms === 60fps animation
    >
      <ReconnectLiveblocksOnMedplumRefresh />
      {children}
    </LiveblocksProviderBase>
  )
}
